/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-sign/useQaItpInlineSign.ts
 * FILE_ID: JOBHUB-QA-ITP-INLINE-SIGN-001
 * PURPOSE: Queue a page choice, draw a signature, then place it on the surface.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { RenderedPage } from '@quotech/email-hub/feature/components/_pdfTypes'
import type { SignPageChoice } from '../itp-export/itpSignPages'

type Signing = {
  sessionPng: Uint8Array | null
  openPad: () => void
  ensureSignBytes: () => Promise<Uint8Array>
  placePreset: (choice: SignPageChoice, pages: RenderedPage[], png?: Uint8Array) => void
  rememberPng: (png: Uint8Array) => void
}

export function attachmentNameForCopy(name: string, kind: 'signed' | 'unsigned'): string {
  if (kind === 'signed') return /-signed\.pdf$/i.test(name) ? name : name.replace(/\.pdf$/i, '-signed.pdf')
  return name.replace(/-signed\.pdf$/i, '.pdf')
}

export function useQaItpInlineSign(signing: Signing) {
  const pagesRef = useRef<RenderedPage[]>([])
  const placed = useRef('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [queued, setQueued] = useState<SignPageChoice | null>(null)
  const [showSurface, setShowSurface] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  const [padSize, setPadSize] = useState<'signature' | 'initials'>('signature')
  const [pageReady, setPageReady] = useState(0)
  const [lastChoice, setLastChoice] = useState<SignPageChoice>('cover')

  const onPages = useCallback((pages: RenderedPage[]) => {
    pagesRef.current = pages
    setPageReady(pages.length)
  }, [])

  useEffect(() => {
    if (!queued || !signing.sessionPng || pagesRef.current.length === 0) return
    const key = `${queued}:${pagesRef.current.length}:${signing.sessionPng.byteLength}`
    if (placed.current === key) return
    placed.current = key
    signing.placePreset(queued, pagesRef.current)
    setQueued(null)
    setShowSurface(true)
  }, [pageReady, queued, signing])

  const choose = (choice: SignPageChoice) => {
    setMenuOpen(false)
    setShowSurface(true)
    placed.current = ''
    void signing.ensureSignBytes()
    setLastChoice(choice)
    if (signing.sessionPng) setQueued(choice)
  }

  const onPadSaved = (png: Uint8Array) => {
    signing.rememberPng(png)
    setQueued(null)
    if (pagesRef.current.length === 0) {
      setQueued(lastChoice)
      return
    }
    signing.placePreset(lastChoice, pagesRef.current, png)
    setShowSurface(true)
  }

  const draw = (size: 'signature' | 'initials') => {
    setPadSize(size)
    signing.openPad()
  }

  return {
    menuOpen,
    setMenuOpen,
    showSurface,
    setShowSurface,
    copyOpen,
    setCopyOpen,
    choose,
    onPadSaved,
    onPages,
    draw,
    padSize,
  }
}
